"use client";

import {
  ArrowRight,
  Bell,
  Book,
  Bookmark,
  Calendar,
  Camera,
  Download,
  FileText,
  Gamepad2,
  Gift,
  Globe,
  Hash,
  Heart,
  HelpCircle,
  Lock,
  Mail,
  MapPin,
  MessageCircle,
  MessageCircleMore,
  MessageSquare,
  Phone,
  Send,
  Shield,
  ShoppingCart,
  Star,
  User,
  Users,
  Video,
} from "lucide-react";
import { motion } from "motion/react";
import type { ComponentType } from "react";
import { Button } from "@/components/ui/button";
import { openExternalUrl } from "@/lib/safe-open";
import { cn } from "@/lib/utils";
import type {
  HomeBlock,
  HomeNamedIcon,
  HomeQuickActionButton,
  WidgetTab,
} from "@/lib/widget-config";
import { BRAND_ICON } from "../../icons/brand-icons";

/** Curated `quick-actions` icon names → a bundled lucide glyph.
 *
 *  Brand channels (telegram / whatsapp / instagram / …) live primarily
 *  in `BRAND_ICON` as real SVGs. The lucide entries below are a
 *  fallback for non-brand action icons (email, chat, lock, …) plus a
 *  graceful degrade if a brand SVG is ever stripped from the bundle.
 *
 *  `Partial<Record<...>>` lets newer `HomeNamedIcon` names (brand-only,
 *  no lucide approximation) skip this map without breaking exhaustivity.
 *  Quick-action renderers fall back to `MessageCircle` when neither
 *  source has a glyph. */
export const NAMED_ICON: Partial<
  Record<HomeNamedIcon, ComponentType<{ size?: number; className?: string }>>
> = {
  telegram: Send,
  whatsapp: MessageSquare,
  viber: Phone,
  instagram: Camera,
  messenger: MessageCircleMore,
  discord: Gamepad2,
  slack: Hash,
  chat: MessageCircle,
  email: Mail,
  phone: Phone,
  "video-call": Video,
  help: HelpCircle,
  book: Book,
  bell: Bell,
  star: Star,
  heart: Heart,
  bookmark: Bookmark,
  "arrow-right": ArrowRight,
  lock: Lock,
  shield: Shield,
  user: User,
  users: Users,
  globe: Globe,
  calendar: Calendar,
  "map-pin": MapPin,
  "shopping-cart": ShoppingCart,
  download: Download,
  gift: Gift,
  "file-text": FileText,
};

export function QuickActionsBlock({
  block,
  onOpenTab,
}: {
  block: Extract<HomeBlock, { kind: "quick-actions" }>;
  onOpenTab: (tab: WidgetTab) => void;
}) {
  // Operator JSON: a block saved without `buttons` renders nothing instead of throwing.
  const buttons = Array.isArray(block.buttons) ? block.buttons : [];
  const runAction = (btn: HomeQuickActionButton) => {
    switch (btn.action) {
      case "open-messenger":
        return onOpenTab("messages");
      case "open-help":
        return onOpenTab("help");
      case "open-news":
        return onOpenTab("news");
      case "open-url":
        openExternalUrl(btn.url);
        return;
    }
  };

  // Inline layout — up to 8 tiles, matching the dashboard's max-buttons
  // setting. Labels render beneath each tile (clamped to one line) so a
  // "WhatsApp" tile still self-identifies, but the dominant visual is
  // the channel mark. With more than 4, tiles wrap into a second row at
  // 4 columns — keeps the per-tile size consistent regardless of count.
  if (block.layout === "inline") {
    const tiles = buttons.slice(0, 8);
    return (
      <div
        className={cn(
          "grid gap-2",
          tiles.length === 1 && "grid-cols-1",
          tiles.length === 2 && "grid-cols-2",
          tiles.length === 3 && "grid-cols-3",
          tiles.length >= 4 && "grid-cols-4",
        )}
      >
        {tiles.map((btn, i) => (
          <QuickActionInlineTile
            // biome-ignore lint/suspicious/noArrayIndexKey: button list is operator-ordered and order-stable
            key={`${btn.action}-${i}`}
            btn={btn}
            onClick={() => runAction(btn)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {buttons.map((btn, i) => (
        <QuickActionButtonView
          // biome-ignore lint/suspicious/noArrayIndexKey: button list is operator-ordered and order-stable
          key={`${btn.action}-${i}`}
          btn={btn}
          onClick={() => runAction(btn)}
        />
      ))}
    </div>
  );
}

/** One inline-layout tile — square icon button with a clamped caption
 *  below. Prefers the real brand SVG over the lucide approximation when
 *  available; long labels stay readable via `truncate` + the native
 *  `title` tooltip on hover. */
function QuickActionInlineTile({
  btn,
  onClick,
}: {
  btn: HomeQuickActionButton;
  onClick: () => void;
}) {
  // Prefer real brand SVG when the icon name matches a known channel
  // (whatsapp / telegram / instagram / messenger / viber / discord /
  // slack / line / facebook / twitter / linkedin / reddit / tiktok).
  // Fall back to the lucide approximation for everything else.
  // `colored` flips the brand mark from monochrome (inherits
  // `--wx-fg`) to its official brand colour / gradient.
  const namedKey = btn.icon?.kind === "named" ? btn.icon.name : null;
  const colored = btn.icon?.kind === "named" ? !!btn.icon.colored : false;
  const BrandGlyph = namedKey ? BRAND_ICON[namedKey] : null;
  const LucideGlyph = namedKey ? (NAMED_ICON[namedKey] ?? MessageCircle) : MessageCircle;
  const Glyph = BrandGlyph ?? LucideGlyph;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2, scale: 1.02 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: "spring", stiffness: 420, damping: 24 }}
      title={btn.label}
      // Fixed-height tile (was `aspect-square`, which stretched tile
      // height to match width — at 2-3 tiles per row the tiles ballooned
      // to 150-200px tall). 96px (`h-24`) is enough room for the 36px
      // icon chip + a single-line caption + chrome padding, regardless
      // of how many tiles share the row.
      className="group flex h-24 flex-col items-center justify-center gap-1.5 rounded-wx-lg bg-wx-bg-elevated px-2 py-3 text-wx-fg transition-colors hover:bg-wx-bg-elevated-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wx-bg transition-transform group-hover:scale-110">
        {btn.icon?.kind === "media" ? (
          // biome-ignore lint/performance/noImgElement: operator-uploaded channel icon of arbitrary host/dimensions; next/image needs known sizes + an allowlist
          <img src={btn.icon.url} alt="" className="h-5 w-5 object-contain" />
        ) : (
          <Glyph
            size={18}
            className={colored ? undefined : "text-wx-fg"}
            colored={BrandGlyph ? colored : undefined}
          />
        )}
      </span>
      {btn.label && (
        <span className="block w-full truncate text-center text-[11px] leading-tight font-medium">
          {btn.label}
        </span>
      )}
    </motion.button>
  );
}

function QuickActionButtonView({
  btn,
  onClick,
}: {
  btn: HomeQuickActionButton;
  onClick: () => void;
}) {
  // Image-only button → render the uploaded media as a tappable card.
  if (!btn.label && btn.icon?.kind === "media") {
    return (
      <motion.button
        type="button"
        onClick={onClick}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.985 }}
        transition={{ type: "spring", stiffness: 360, damping: 26 }}
        className="block w-full overflow-hidden rounded-wx-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary"
      >
        {/* biome-ignore lint/performance/noImgElement: operator-uploaded button media of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
        <img src={btn.icon.url} alt="" className="w-full object-cover" />
      </motion.button>
    );
  }

  const variant = btn.style === "primary" ? "solid" : btn.style === "ghost" ? "ghost" : "tonal";
  const icon =
    btn.icon?.kind === "named" ? (
      (() => {
        // Prefer the real brand SVG; fall back to the lucide glyph.
        // `colored` only applies to the brand SVGs (lucide is always
        // monochrome via `currentColor` regardless).
        const name = btn.icon.name;
        const isColored = !!btn.icon.colored;
        const Brand = BRAND_ICON[name];
        if (Brand) return <Brand size={16} className="shrink-0" colored={isColored} />;
        const Lucide = NAMED_ICON[name] ?? MessageCircle;
        return <Lucide size={16} className="shrink-0" />;
      })()
    ) : btn.icon?.kind === "media" ? (
      // biome-ignore lint/performance/noImgElement: operator-uploaded button icon of arbitrary host/dimensions; next/image needs known sizes + an allowlist
      <img src={btn.icon.url} alt="" className="h-4 w-4 shrink-0 object-contain" />
    ) : null;

  return (
    <Button
      type="button"
      variant={variant}
      size="lg"
      onClick={onClick}
      className="justify-start gap-2.5"
    >
      {icon}
      {btn.label && <span className="text-sm font-medium">{btn.label}</span>}
    </Button>
  );
}
